import { LoadingButton } from "@mui/lab";
import {
    Alert,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { useContext, useState } from "react";
import { useSelector } from "react-redux";
import { AccountService, CategorizationInput } from "../../../api/psc";
import { DialogHeader, MultiSelect } from "../../../components/exports";
import DialogContainer from "../../../components/shared/DialogContainer";
import { DialogContext } from "../../../contexts/DialogueContext";
import { RootState } from "../../../slices/Store";

type TAnswer = {
    type: null | number;
    description: string;
    approachPhaseId: number[];
    salePhaseId: number[];
};

export default function CategorizationDialog() {
    const selectionLabel = "انتخاب کنید...";
    const textLabel = "زمینه‌ کاری...";
    const title = "نظرسنجی";

    const dialogContext = useContext(DialogContext);

    const sharedState = useSelector((state: RootState) => state.shared);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const availablePhases = sharedState.baseInfo.phaseList;

    const [answers, setAnswers] = useState<TAnswer>({
        type: null,
        description: "",
        approachPhaseId: [],
        salePhaseId: [],
    });

    const answerDescription = (ans: string) => {
        setAnswers((prev) => ({ ...prev, description: ans }));
    };

    const answerApproachPhaseIds = (ids: number[]) => {
        setAnswers((prev) => ({
            ...prev,
            approachPhaseId: ids,
        }));
    };

    const answerSalePhaseIds = (ids: number[]) => {
        setAnswers((prev) => ({
            ...prev,
            salePhaseId: ids,
        }));
    };

    const answerType = (selectedId: number) => {
        setAnswers((prev) => ({
            ...prev,
            type: selectedId,
        }));
    };

    const handleSave = async () => {
        if (
            answers.type == null ||
            answers.description == "" ||
            answers.approachPhaseId.length == 0 ||
            answers.salePhaseId.length == 0
        ) {
            setErrorMessage("لطفا به همه موارد پاسخ دهید.");
            return;
        }

        setIsSubmitting(true);

        try {
            const body: CategorizationInput = {
                description: answers.description,
                approachPhaseIds: answers.approachPhaseId,
                salePhaseIds: answers.salePhaseId,
                type: answers.type,
            };
            await AccountService.setCategorization("1", body);
        } finally {
            setIsSubmitting(false);
            dialogContext.closeDialog();
        }
    };

    return (
        <DialogContainer preferredWith={700}>
            <DialogHeader title={title} action={<></>} />

            <Stack sx={{ py: 3, px: 2 }} spacing={3}>
                <Stack spacing={1}>
                    <Typography variant="h6">کاربر عزیز</Typography>
                    <Typography sx={{ textAlign: "justify" }}>
                        ما در پرشین‌سازه همواره تلاش می‌کنیم که خدماتی بهینه و
                        متناسب با نیاز روز به شما ارائه کنیم. در این راستا و به
                        منظور شناخت بهتر بازار، کار شما و دغدغه‌هایتان، و
                        ارائه‌ی راهکارهای متناسب با نیازهای شما 3 پرسش پیش رو
                        طراحی شده است. سپاسگزارتان خواهیم بود که با دقت و کامل
                        پاسخگوی ما باشید.
                    </Typography>
                </Stack>

                <Stack direction="column" spacing={1}>
                    <Typography>لطفا نوع فعالیت خود را انتخاب کنید.</Typography>
                    <FormControl>
                        <InputLabel>{selectionLabel}</InputLabel>
                        <Select
                            label={selectionLabel}
                            value={answers.type}
                            onChange={(e) =>
                                answerType(e.target.value as number)
                            }
                        >
                            <MenuItem value={1}>فروش</MenuItem>
                            <MenuItem value={2}>اجرا</MenuItem>
                            <MenuItem value={3}>هردو</MenuItem>
                        </Select>
                    </FormControl>
                </Stack>

                <Stack direction="column" spacing={1}>
                    <Typography>
                        لطفا زمینه/زمینه‌های کاری خود را کامل و با جزییات بیان
                        کنید و بفرمایید که چه خدمات و محصولاتی ارائه می‌دهید.
                    </Typography>
                    <TextField
                        multiline
                        rows={5}
                        maxRows={5}
                        minRows={5}
                        label={textLabel}
                        value={answers.description}
                        onChange={(e) => answerDescription(e.target.value)}
                    />
                </Stack>

                <Stack direction="column" spacing={1}>
                    <Typography>
                        ⁠در کدام مراحل ساختمانی مشتریان به خدمات و محصولات شما
                        نیاز پیدا کرده و خرید نهایی را انجام می‌دهند؟ (می‌توانید
                        چند مرحله را انتخاب کنید)
                    </Typography>

                    <MultiSelect
                        values={availablePhases}
                        onValueChange={answerSalePhaseIds}
                        label={selectionLabel}
                        selectedIds={answers.salePhaseId}
                    />
                </Stack>

                <Stack direction="column" spacing={1}>
                    <Typography>
                        شما ⁠در کدام مراحل ساختمانی به سراغ مشتریان می‌روید و با
                        آنها ارتباط برقرار می‌کنید؟ (می‌توانید چند مرحله را
                        انتخاب کنید)
                    </Typography>
                    <MultiSelect
                        values={availablePhases}
                        onValueChange={answerApproachPhaseIds}
                        label={selectionLabel}
                        selectedIds={answers.approachPhaseId}
                    />
                </Stack>

                {errorMessage !== null && (
                    <Alert severity="error">{errorMessage}</Alert>
                )}

                <LoadingButton
                    onClick={handleSave}
                    loading={isSubmitting}
                    variant="contained"
                >
                    ذخیره
                </LoadingButton>
            </Stack>
        </DialogContainer>
    );
}
